import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import {
  debounce,
  isEmpty,
  isEqual,
  omit,
  truncate,
  uniq,
  uniqBy,
} from 'lodash';
import { formatMoney } from 'accounting';
import {
  Condition,
  CreditRating,
  DEFAULT_DOWN_PAYMENT,
  FinanceType,
  OfferCategoryType,
  TaxType,
  CLIENT_TYPE,
} from '@upstart/harmony/lib/constants';
import {
  IncentiveType,
  getProgramOffersWithTypes,
} from '@upstart/harmony/lib/incentive-utils';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { fmtPercent } from '@upstart/harmony/lib/formatter';
import { DealerLaunchStepProps } from '../types';
import {
  CashOfferList,
  IncentivesProgramOffer,
  IncentivesWithType,
  LeaseOfferList,
  OfferList,
  Payments,
  PurchaseOfferList,
} from '../../../types/paymentTypes';
import { Car } from '../../../types/carTypes';
import {
  FinanceTypePricingValidation,
  PricingIncorrectReason,
  PricingIncorrectState,
  PricingValidationClientType,
  PricingValidationType,
} from '../../../types/dealerTypes';
import { Deal } from '../../../types/dealTypes';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { CarService } from '../../../services/CarService';
import { DealService } from '../../../services/DealService';
import { LaunchPadService } from '../../../services/LaunchPadService';
import { PaymentsService } from '../../../services/PaymentsService';
import PencilService from '../../../services/PencilService';
import UtilsService from '../../../services/UtilsService';
import { Button } from '../../Input/Button/Button';
import { Select } from '../../Input/Select/Select';
import { TextField } from '../../Input/TextField/TextField';
import { Checkbox } from '../../Input/Checkbox/Checkbox';
import { Radio } from '../../Input/Radio/Radio';
import { LinkButton } from '../../Input/Button/LinkButton';
import { ROUTES } from '../../../utils/routes';
import { zipValidator } from '../../../utils/validation';
import { Tabs, TabsRef } from '../../Tabs/Tabs';
import styles from '../styles.module.scss';
import imageCarPlaceholder from '../../../assets/car-placeholder.png';
import { Spinner } from '../../Spinner/Spinner';
import { TooltipIcon } from '../../TooltipIcon/TooltipIcon';
import { useFormBlock } from '../../../hooks/useFormBlock';

// A custom hook that builds on useLocation to parse
// the query string for you.
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Predicate Typeguard
function isPurchaseOfferList(offer: OfferList): offer is PurchaseOfferList {
  return offer.financeType === FinanceType.PURCHASE;
}

// Predicate Typeguard
function isLeaseOfferList(offer: OfferList): offer is LeaseOfferList {
  return offer.financeType === FinanceType.LEASE;
}

// Predicate Typeguard
function isCashOfferList(offer: OfferList): offer is CashOfferList {
  return offer.financeType === FinanceType.CASH;
}

const creditRatingOptions = [
  {
    label: 'Excellent',
    value: CreditRating.EXCELLENT,
  },
  {
    label: 'Good',
    value: CreditRating.GOOD,
  },
  {
    label: 'Below Average',
    value: CreditRating.AVERAGE,
  },
  {
    label: 'Poor',
    value: CreditRating.POOR,
  },
];

const pricingIncorrectReasonMap: Record<PricingIncorrectReason, string> = {
  [PricingIncorrectReason.VEHICLE_PRICE_WRONG]: 'Vehicle Price is wrong',
  [PricingIncorrectReason.FEES_INCORRECT]: 'Fees are not correct',
  [PricingIncorrectReason.TAXES_INCORRECT]: 'Taxes are not correct',
  [PricingIncorrectReason.OTHER]: 'Other',
};

const validationTypeToClientTypeMap: Record<
  PricingValidationClientType,
  CLIENT_TYPE
> = {
  [PricingValidationClientType.INSTORE]: CLIENT_TYPE.IN_STORE,
  [PricingValidationClientType.ONLINE]: CLIENT_TYPE.ONLINE,
};

const clientTypeToLabel: Record<PricingValidationClientType, string> = {
  [PricingValidationClientType.INSTORE]: 'In Store',
  [PricingValidationClientType.ONLINE]: 'Online',
};

const PRICING_INCORRECT_REASONS_SORTED = [
  PricingIncorrectReason.VEHICLE_PRICE_WRONG,
  PricingIncorrectReason.FEES_INCORRECT,
  PricingIncorrectReason.TAXES_INCORRECT,
  PricingIncorrectReason.OTHER,
];

const columnSpacerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
};

export const DealerLaunchPricingValidation: FunctionComponent<DealerLaunchStepProps<{
  validationType: PricingValidationType;
  clientType: PricingValidationClientType;
  isLastPricingValidationForm?: boolean;
}>> = ({
  dealer,
  prevStep,
  nextStep,
  step: {
    stepProps: { validationType, clientType, isLastPricingValidationForm },
  },
}) => {
  const form = useLaunchpadForm<{
    stock: string;
  }>();

  const tabsRef = useRef<TabsRef>(null);
  const query = useQuery();

  const [sampleCar, setSampleCar] = useState<Car>();
  const [sampleCarStockInputValue, setSampleCarStockInputValue] = useState<
    string
  >();
  const [
    carStockParameterForRemoteRequest,
    setCarStockParameterForRemoteRequest,
  ] = useState<string>();
  const [payments, setPayments] = useState<Payments>();
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [term, setTerm] = useState<number>();
  const [leaseTerm, setLeaseTerm] = useState<number>();
  const [mileage, setMileage] = useState<number>();
  const [initialDeal, setInitialDeal] = useState<any>();
  const [deal, setDeal] = useState<any>();
  const [pencil, setPencil] = useState<any>();
  const [downPayment, setDownPayment] = useState<number>(0);
  const [tradeAllowance, setTradeAllowance] = useState<number>(0);
  const [incentivesAndRebates, setIncentivesAndRebates] = useState<number>(0);
  const [lowInterestOrHighRebate, setLowInterestOrHighRebate] = useState<
    OfferCategoryType.LOW_INTEREST | OfferCategoryType.HIGH_REBATE
  >(OfferCategoryType.LOW_INTEREST);
  const [emailLinkModalOpen, setEmailLinkModalOpen] = useState(false);
  const [pricingNotCorrectModalOpen, setPricingNotCorrectModalOpen] = useState(
    false,
  );
  const [zip, setZip] = useState<string | null>();
  const [previousWorkingZip, setPreviousWorkingZip] = useState<string | null>();
  const [zipInputValue, setZipInputValue] = useState<string | null>();
  const [creditRating, setCreditRating] = useState<CreditRating>(
    CreditRating.EXCELLENT,
  );
  const [selectedPrograms, setSelectedPrograms] = useState<
    {
      [index in FinanceType]?: {
        [index2 in string]: IncentivesProgramOffer;
      };
    }
  >();

  const sampleCarRequest = useAsync(CarService.getSampleCar, [
    dealer.id,
    carStockParameterForRemoteRequest,
    validationType,
  ]);

  const countiesAndCities = useAsyncCallback(UtilsService.getCountiesAndCities);

  const pricingValidationDefaultState = {
    [FinanceType.CASH]: {
      correct: true,
      reasons: {},
    },
    [FinanceType.PURCHASE]: {
      correct: true,
      reasons: {},
    },
    [FinanceType.LEASE]: {
      correct: true,
      reasons: {},
    },
  };

  const pricingValidationOriginalState =
    dealer.launchpad?.pricingValidation?.[validationType]?.[clientType];
  const pricingValidationState =
    pricingValidationOriginalState || pricingValidationDefaultState;

  const [wrongPricingValidationText, setWrongPricingValidationText] = useState<
    string
  >('');

  const [
    financeTypePricingValidation,
    setFinanceTypePricingValidation,
  ] = useState<FinanceTypePricingValidation>(pricingValidationState);

  const { formBlock, updateBlock } = useFormBlock({
    isDirty: false,
    message:
      'This page has unsaved changes on it, are you sure you want to leave?',
  });

  // We are not using react-form on this component, so it won't have built in functionality to block user from leaving / refreshing page to precent losing changes to form
  // Workaround is to use a custom hook that exposes a 'uploadBlock' method to set isDirty so we block navigation if there are form changes
  // Note that isSubmitting must be passed in 'uploadBlock' when user submits the form
  useEffect(() => {
    if (!isEqual(pricingValidationState, financeTypePricingValidation)) {
      updateBlock({
        isDirty: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeTypePricingValidation]);

  const [programOfferCounter, setProgramOfferCounter] = useState(0);

  const isPricingWrong = () =>
    wrongPricingValidationText ||
    !financeTypePricingValidation.cash.correct ||
    !financeTypePricingValidation.purchase.correct ||
    !financeTypePricingValidation.lease.correct;

  const submitWrongPricingValidationEmail = async (
    pricingReason: PricingIncorrectState,
    financeType: FinanceType,
  ) => {
    // get the wrong pricing reasons and map it to a string[] array
    const wrongPricingValidationReasons = [
      PricingIncorrectReason.VEHICLE_PRICE_WRONG,
      PricingIncorrectReason.TAXES_INCORRECT,
      PricingIncorrectReason.FEES_INCORRECT,
    ]
      .filter((x) => !!pricingReason.reasons?.[x]?.value)
      .map((r) => pricingReason.reasons?.[r]?.message || '');

    await LaunchPadService.sendWrongPricingText(
      dealer.id,
      clientType,
      validationType,
      financeType,
      wrongPricingValidationReasons,
      pricingReason.reasons?.other?.message || '',
      sampleCar?.stock || sampleCar?.id || '',
    );
  };

  const submitWrongPricingValidationText = useAsyncCallback(
    async function submitWrongPricingAsyncCallback() {
      if (isPricingWrong()) {
        // validate each of the finance types for incorrect pricing & send an email is correct flag is set to false
        if (!financeTypePricingValidation.cash.correct) {
          await submitWrongPricingValidationEmail(
            financeTypePricingValidation.cash,
            FinanceType.CASH,
          );
        }

        if (!financeTypePricingValidation.purchase.correct) {
          await submitWrongPricingValidationEmail(
            financeTypePricingValidation.purchase,
            FinanceType.PURCHASE,
          );
        }

        if (!financeTypePricingValidation.lease.correct) {
          await submitWrongPricingValidationEmail(
            financeTypePricingValidation.lease,
            FinanceType.LEASE,
          );
        }

        toast('Pricing issue feedback submitted. Thank you!');
        // set pricing reasons to default state after submit
        setWrongPricingValidationText('');
        setPricingNotCorrectModalOpen(false);
      }
    },
  );

  const sampleCarResult = sampleCarRequest.result?.car;

  // creates a deal when component is mounted & sets state in 'initialDeal'
  // show modal if opening link from email link
  // set zip input fields from dealer object
  useEffect(() => {
    // Create a deal when this component is mounted
    async function createDeal() {
      const newDeal = await DealService.createDeal();
      setInitialDeal(newDeal);

      // potentially remove deal when component is unmounted????
    }
    createDeal();
    // Show additional help modal if opening from the email link
    if (query.get('emailLink') === 'true') {
      setEmailLinkModalOpen(true);
    }

    setZip(dealer.zip);
    setPreviousWorkingZip(dealer.zip);
    setZipInputValue(dealer.zip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRebatesAndIncentives = (financeType: FinanceType) => {
    let totalAmount = 0;
    Object.values(selectedPrograms?.[financeType] || {}).forEach((x) => {
      totalAmount += x.cash ?? 0;
    });
    return totalAmount;
  };

  const getPayments = async (
    sampleCarResultLocal: Car,
    initialDealLocal: Deal,
    dealLocal: Deal,
    downPaymentLocal: number,
  ) => {
    if (sampleCarResultLocal) {
      setIsLoadingPayments(true);
      const carChanged =
        (dealLocal || initialDealLocal || {}).car_id !==
        sampleCarResultLocal.id;
      const creditRatingChanged =
        (dealLocal || initialDealLocal || {}).creditRating !== creditRating;
      const updatedDeal = await DealService.updateDeal({
        ...(dealLocal || initialDealLocal),
        car_id: sampleCarResultLocal.id,
        creditRating,
        // selectedProgramOffers: Object.values(selectedPrograms || {}),
      });
      setDeal(updatedDeal);
      let localPencil = pencil;
      if (!localPencil || carChanged || creditRatingChanged) {
        const newPencil = await PencilService.createPencil(updatedDeal.id, {
          dealId: updatedDeal.id,
          carId: sampleCarResultLocal.id,
          creditRating,
        });
        setPencil(newPencil);
        localPencil = newPencil;
      }

      // get incentives from state & apply to pencil
      const cashIncentives = getRebatesAndIncentives(FinanceType.CASH);
      const purchaseIncentives = getRebatesAndIncentives(FinanceType.PURCHASE);
      const leaseIncentives = getRebatesAndIncentives(FinanceType.LEASE);

      localPencil.data.cash.forEach(
        // eslint-disable-next-line no-param-reassign,no-return-assign
        (offer: any) => (offer.rebate = cashIncentives),
      );
      localPencil.data.purchase.forEach(
        // eslint-disable-next-line no-param-reassign,no-return-assign
        (offer: any) => (offer.rebate = purchaseIncentives),
      );
      localPencil.data.lease.forEach(
        // eslint-disable-next-line no-param-reassign,no-return-assign
        (offer: any) => (offer.rebate = leaseIncentives),
      );
      const updatedPencil = await PencilService.updatePencil(
        localPencil.id,
        localPencil,
      );
      setPencil(updatedPencil);
      const onlinePayments = await PaymentsService.getPayments(
        dealLocal?.id || initialDealLocal?.id,
        downPaymentLocal,
        creditRating,
        localPencil.id,
        validationTypeToClientTypeMap[clientType],
        true,
      );
      setPayments(onlinePayments);

      const purchaseOffer = onlinePayments?.allOffers?.find(
        isPurchaseOfferList,
      );

      const offerCategories = uniq(
        purchaseOffer?.offers?.map((o) => (o.category || {}).type),
      );

      const hasLowInterestAndHighRebate = offerCategories.length > 1;

      const purchaseTerm = purchaseOffer?.offers?.filter(
        (offer: any) =>
          !hasLowInterestAndHighRebate ||
          (offer.category || {}).type === lowInterestOrHighRebate,
      )[0]?.months;
      setTerm(purchaseTerm);

      const leaseOffer = onlinePayments.allOffers?.find(isLeaseOfferList);
      const leaseMileage = leaseOffer?.offers?.[0]?.mileage;
      setMileage(leaseMileage);
      setLeaseTerm(leaseOffer?.offers?.[0]?.months);

      setIsLoadingPayments(false);
    }
  };

  // validate that zip is correct
  // if correct, lets update the payment with the new zip
  useEffect(() => {
    async function executeEffect() {
      if (zip && zipValidator.value.test(zip) && deal) {
        const { data } = await countiesAndCities.execute(zip);
        if (isEmpty(data)) {
          setZipInputValue(previousWorkingZip);
          toast(
            `ZIP ${zip} is invalid, reverting to ZIP ${previousWorkingZip}`,
            {
              type: 'warning',
            },
          );
          return;
        }
        const updatedDeal = {
          ...deal,
          ...{
            applicant: {
              zip,
              county: data[0].county,
              city: data[0].city,
              state: data[0].state,
            },
          },
        };
        if (sampleCarResult) {
          getPayments(sampleCarResult, initialDeal, updatedDeal, downPayment);
          setPreviousWorkingZip(zip);
        }
      }
    }
    executeEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);

  const debouncedUpdateTradeAllowance = useRef(
    debounce(
      async (
        sampleCarResultLocal: any,
        initialDealLocal: any,
        newDeal: Deal,
        downPaymentLocal: number,
      ) => {
        await getPayments(
          sampleCarResultLocal,
          initialDealLocal,
          newDeal,
          downPaymentLocal,
        );
      },
      1000,
    ),
  );

  const debouncedUpdateDownPayment = useRef(debounce(setDownPayment, 1000));
  const debouncedUpdateIncentivesAndRebates = useRef(
    debounce(setIncentivesAndRebates, 1000),
  );

  const debouncedUpdateProgramOfferCount = useRef(
    debounce(setProgramOfferCounter, 1000),
  );

  // selected offer is the one from payments endpoint
  // we merge with the incentiveOffers from car object and render back to the user
  // you just tally in incentive and mark it done

  useEffect(() => {
    if (!initialDeal) {
      return;
    }
    if (sampleCarResult && initialDeal) {
      // Set the stock input - so it will be filled in when page first load
      setSampleCarStockInputValue(sampleCarResult?.stock || '');
      //
      if (!sampleCar || sampleCar.id !== sampleCarResult.id) {
        setDownPayment(
          Math.round((sampleCarResult?.price || 0) * DEFAULT_DOWN_PAYMENT),
        );
      }
      setSampleCar(sampleCarResult);
      getPayments(sampleCarResult, initialDeal, deal, downPayment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sampleCarResult,
    downPayment,
    initialDeal,
    creditRating,
    incentivesAndRebates,
    programOfferCounter,
  ]);

  const onSubmit = async (e?: any) => {
    e?.preventDefault();
    // we need to update formBlock ref that form is is submitting, so as to not trigger a warning message that the user will lose changes made in form
    // because we are navigating away from page
    updateBlock({
      isSubmitting: true,
    });
    await submitWrongPricingValidationText.execute();
    await nextStep({
      launchpad: {
        ...(isLastPricingValidationForm && {
          status: { finishedPricingValidation: true },
        }),
        pricingValidation: {
          [validationType]: {
            [clientType]: financeTypePricingValidation,
          },
        },
      },
    });
  };

  const goToPreviousTab = (currentFinanceType: FinanceType) => {
    switch (currentFinanceType) {
      case FinanceType.CASH:
        prevStep();
        break;
      case FinanceType.LEASE:
        tabsRef.current?.setActiveChildByKey(FinanceType.PURCHASE);
        break;
      case FinanceType.PURCHASE:
        tabsRef.current?.setActiveChildByKey(FinanceType.CASH);
        break;
      default:
    }
  };

  const confirmAndGoToNextTab = async (
    currentFinanceType: FinanceType,
    hasLeaseForm = false,
  ) => {
    // lease tab is the last tab & if we are on lease tab we move to next step
    // if we don't have a lease tab, then purchase is the last tab and we move to next step after clicking next tab
    switch (currentFinanceType) {
      case FinanceType.CASH:
        tabsRef.current?.setActiveChildByKey(FinanceType.PURCHASE);
        break;
      case FinanceType.PURCHASE:
        if (!hasLeaseForm) {
          await onSubmit();
          break;
        }
        tabsRef.current?.setActiveChildByKey(FinanceType.LEASE);
        break;
      case FinanceType.LEASE:
      default:
        await onSubmit();
    }
  };

  const renderPriceBreakdownLine = (
    label: string,
    price: number | null | undefined,
    subItem?: boolean,
  ) => (
    <div key={`${label}`} className={styles.priceBreakdownLine}>
      <h2
        className={classNames(
          styles.priceBreakdownLeft,
          subItem ? styles.subItem : undefined,
        )}
      >
        {label}
      </h2>
      <h2 className={styles.priceBreakdownMiddle}>$</h2>
      <h2 className={styles.priceBreakdownRight}>
        {formatMoney(price || 0, '')}
      </h2>
    </div>
  );

  const renderPriceBreakdownLineRate = (
    rate: number | null | undefined,
    subItem?: boolean,
  ) => (
    <div className={styles.priceBreakdownLine}>
      <h2
        className={classNames(
          styles.priceBreakdownLeft,
          subItem ? styles.subItem : undefined,
        )}
      >
        Money Factor
        <TooltipIcon
          invertedColor
          content="The money factor is the sum of the lease rate from the manufacturer and the markup set in launchpad previously"
        />
      </h2>
      <h2 className={styles.priceBreakdownRight}>{(rate || 0).toFixed(6)}</h2>
    </div>
  );

  const renderPriceBreakdownLineBigNumber = (
    price: number | null | undefined,
  ) => (
    <div className={styles.priceBreakdownLineBig}>
      <h1 className={styles.priceBreakdownLeft}>$</h1>
      <h1 className={styles.priceBreakdownright}>
        {formatMoney(price || 0, '')}
      </h1>
    </div>
  );

  const renderPriceBreakdownCustomLine = (
    label: string,
    component?: JSX.Element,
    tooltip?: string,
    detail?: JSX.Element,
  ) => (
    <>
      <div className={styles.priceBreakdownLine}>
        <h2 className={styles.priceBreakdownLeft}>
          {label}
          {tooltip && <TooltipIcon content={tooltip} invertedColor />}
        </h2>
        <h2 className={styles.priceBreakdownright}>{component}</h2>
      </div>
      {detail && detail}
    </>
  );

  const renderPriceBreakdownDownPayment = () => {
    const customComponent = (
      <div className={classNames(styles.customInput)}>
        <div className={styles.left}>$</div>
        <input
          className={classNames(styles.right, styles.customPriceInput)}
          name="downPayment"
          defaultValue={`${downPayment}`}
          onChange={(val) => {
            debouncedUpdateDownPayment.current(
              parseInt(val.target.value || '0', 10),
            );
          }}
        />
      </div>
    );
    return renderPriceBreakdownCustomLine('Down Payment', customComponent);
  };

  const updateRebatesAndIncentives = (
    programOffer: IncentivesProgramOffer,
    financeType: FinanceType,
  ) => {
    const existing = { ...selectedPrograms?.[financeType] };
    const newSelection = existing?.[programOffer.id]
      ? omit(existing, [programOffer.id])
      : {
          ...existing,
          [programOffer.id]: programOffer,
        };
    const newSelectedPrograms = {
      ...selectedPrograms,
      [financeType]: newSelection,
    };
    setSelectedPrograms(newSelectedPrograms);
    debouncedUpdateProgramOfferCount.current(programOfferCounter + 1);
  };

  const renderPriceBreakdownIncentivesAndRebates = (
    financeType: FinanceType,
    programs: IncentivesWithType,
  ) => {
    const incentiveAmount = getRebatesAndIncentives(financeType) ?? 0;
    const offerProgramsComponent = (
      <>
        {renderPriceBreakdownLine('Incentives & Rebates', incentiveAmount)}
        {programs?.map(({ programOffer }) => {
          return (
            <>
              <div
                className={classNames(
                  styles.priceBreakdownLine,
                  styles.incentivesLine,
                )}
              >
                <div className={styles.priceBreakdownLeft}>
                  <div style={columnSpacerStyle}>
                    <Checkbox
                      key={`offer-program-${programOffer.id}`}
                      title={programOffer.program.description}
                      label={truncate(programOffer.program.description, {
                        length: 100,
                        separator: '...',
                      })}
                      name={`offer-program-${programOffer.id}`}
                      className={classNames(styles.checkboxCustom, styles.left)}
                      value={
                        selectedPrograms?.[financeType]?.[programOffer.id] !==
                        undefined
                      }
                      onChange={() =>
                        updateRebatesAndIncentives(programOffer, financeType)
                      }
                    />
                    <span
                      className={styles.priceBreakdownLeft}
                      style={{ paddingLeft: '42px' }}
                    >
                      Expires:
                    </span>
                  </div>
                </div>
                <h2 className={styles.priceBreakdownMiddle}>$</h2>
                <div
                  className={classNames(
                    styles.value,
                    styles.priceBreakdownRight,
                  )}
                >
                  <div style={columnSpacerStyle}>
                    <span>{programOffer.cash}</span>
                    <span>{programOffer.program.endDate}</span>
                  </div>
                </div>
              </div>
            </>
          );
        })}
      </>
    );
    const customComponent = (defaultValue: string) => (
      <div className={classNames(styles.customInput)}>
        <div className={styles.left}>$</div>
        <input
          className={classNames(styles.right, styles.customPriceInput)}
          name="downPayment"
          defaultValue={defaultValue}
          onChange={(val) => {
            debouncedUpdateIncentivesAndRebates.current(
              parseInt(val.target.value || '0', 10),
            );
          }}
        />
      </div>
    );

    return (
      <>
        {programs
          ? offerProgramsComponent
          : renderPriceBreakdownCustomLine(
              'Incentives and Rebates',
              customComponent(`$${incentiveAmount}`),
              "No incentives or offers will be displayed in this tool by default. You can use the field at right to enter the value of an incentive or offer you'd like to apply to this example. Payments will automatically adjust based on the amount entered.",
            )}
      </>
    );
  };

  const renderPriceBreakdownZip = () => {
    const component = (
      <div className={styles.customZipContainer}>
        <div className={styles.customInput} style={{ marginRight: '10px' }}>
          <div className={styles.left}>ZIP</div>
          <input
            className={classNames(styles.right, styles.customZipInput)}
            name="zip"
            value={zipInputValue || ''}
            onChange={(val) => setZipInputValue(val.target.value)}
          />
        </div>
        <button
          type="button"
          className={classNames(styles.button, styles.minimal)}
          onClick={() => {
            const zipInput = zipInputValue || '';
            if (zipValidator.value.test(zipInput)) {
              setPreviousWorkingZip(zip);
              setZip(zipInput);
              return;
            }
            setZipInputValue(zip);
            toast(`ZIP ${zipInput} is invalid, reverting to ZIP ${zip}`, {
              type: 'warning',
            });
          }}
        >
          Update
        </button>
      </div>
    );
    return renderPriceBreakdownCustomLine('Taxes', component);
  };

  const renderPriceBreakdownTradeInAllowance = () => {
    const customComponent = (
      <div className={classNames(styles.customInput)}>
        <div className={styles.left}>$</div>
        <input
          className={classNames(styles.right, styles.customPriceInput)}
          name="tradeAllowance"
          defaultValue={`${tradeAllowance}`}
          onChange={(val) => {
            const newDeal = {
              ...deal,
              tradeIn: parseInt(val.target.value || '0', 10) || null,
            };
            setTradeAllowance(parseInt(val.target.value || '0', 10));
            debouncedUpdateTradeAllowance.current(
              sampleCarResult,
              initialDeal,
              newDeal,
              downPayment,
            );
          }}
        />
      </div>
    );
    return renderPriceBreakdownCustomLine('Trade Allowance', customComponent);
  };

  const currentPurchaseOffer = payments?.allOffers?.find(isPurchaseOfferList);

  const offerCategories = uniq(
    currentPurchaseOffer?.offers.map((o: any) => (o.category || {}).type),
  );

  const hasLowInterestAndHighRebate = offerCategories.length > 1;

  const updatePricingReason = (
    reason: PricingIncorrectReason,
    financeType: FinanceType,
    inverseSelection = true,
    other?: string,
  ) => {
    const newFinanceTypePricingValidation = { ...financeTypePricingValidation };
    const financeTypePricingReason =
      newFinanceTypePricingValidation[financeType];

    const existingValue = financeTypePricingReason.reasons?.[reason]?.value;
    const inverseValue = existingValue ? !existingValue : true;

    newFinanceTypePricingValidation[financeType] = {
      correct: financeTypePricingReason.correct,
      reasons: {
        ...financeTypePricingReason.reasons,
        [reason]: {
          value: inverseSelection ? inverseValue : existingValue,
          message:
            reason === PricingIncorrectReason.OTHER
              ? other
              : pricingIncorrectReasonMap?.[reason],
        },
      },
    };
    setFinanceTypePricingValidation(newFinanceTypePricingValidation);
  };

  const renderPricingCorrect = (financeType: FinanceType) => {
    const financeTypePricingReason = financeTypePricingValidation[
      financeType
    ] || { correct: true, reasons: {} };
    const isPricingRight = financeTypePricingReason.correct;
    return (
      <div className={styles.pricingIncorrectReason}>
        <h3>Is this pricing correct?</h3>
        <div>
          <Radio
            className="flex-column"
            key="pricing-incorrect-yesno"
            name={`${validationType}.${clientType}.${financeType}`}
            options={[
              { label: "Yes, it's correct", value: true },
              { label: 'Not right now', value: false },
            ]}
            value={financeTypePricingReason.correct}
            onChange={() => {
              const newFinanceTypePricingReason = {
                ...financeTypePricingReason,
              };
              newFinanceTypePricingReason.correct = !newFinanceTypePricingReason.correct;
              setFinanceTypePricingValidation({
                ...financeTypePricingValidation,
                [financeType]: newFinanceTypePricingReason,
              });
            }}
          />
        </div>
        {!isPricingRight && (
          <div className={styles.options}>
            {PRICING_INCORRECT_REASONS_SORTED.map((reason, idx) => (
              <Checkbox
                key={`pricing-incorrect-${reason}`}
                label={pricingIncorrectReasonMap[reason]}
                className={styles.option}
                name={`reason-${idx}`}
                value={financeTypePricingReason.reasons?.[reason]?.value}
                onChange={() => updatePricingReason(reason, financeType, true)}
              />
            ))}
          </div>
        )}
        {!isPricingRight &&
          financeTypePricingReason?.reasons?.[PricingIncorrectReason.OTHER]
            ?.value && (
            <textarea
              cols={30}
              rows={5}
              className={styles.wrongPricingTextArea}
              value={financeTypePricingReason.reasons.other?.message}
              placeholder="Feel free to include more details ..."
              onChange={(e: any) => {
                setWrongPricingValidationText(e.target.value);
                updatePricingReason(
                  PricingIncorrectReason.OTHER,
                  financeType,
                  false,
                  e.target.value,
                );
              }}
            />
          )}
      </div>
    );
  };

  const getPaymentOfferDetails = (financeType: FinanceType) => {
    const leaseOffer =
      financeType === FinanceType.LEASE
        ? payments?.allOffers
            ?.find(isLeaseOfferList)
            ?.offers.find(
              (offer: any) =>
                offer.mileage === mileage && offer.months === leaseTerm,
            )
        : null;
    const mileages = uniq(
      financeType === FinanceType.LEASE
        ? payments?.allOffers
            ?.find(isLeaseOfferList)
            ?.offers.map((offer: any) => offer.mileage)
        : [],
    );
    const purchaseOffer =
      financeType === FinanceType.PURCHASE
        ? payments?.allOffers
            ?.find(isPurchaseOfferList)
            ?.offers.filter(
              (offer: any) =>
                !hasLowInterestAndHighRebate ||
                (offer.category || {}).type === lowInterestOrHighRebate,
            )
            .find((offer: any) => offer.months === term)
        : null;
    const terms = uniq(
      financeType === FinanceType.PURCHASE && !isLoadingPayments
        ? payments?.allOffers
            ?.find(isPurchaseOfferList)
            ?.offers.filter(
              (offer: any) =>
                !hasLowInterestAndHighRebate ||
                (offer.category || {}).type === lowInterestOrHighRebate,
            )
            .map((offer: any) => offer.months)
        : [],
    );
    const leaseTerms = uniq(
      financeType === FinanceType.LEASE && !isLoadingPayments
        ? payments?.allOffers
            ?.find(isLeaseOfferList)
            ?.offers.map((offer: any) => offer.months)
        : [],
    );
    const cashOffer =
      financeType === FinanceType.CASH
        ? payments?.allOffers?.find(isCashOfferList)?.offers[0]
        : null;

    // selected offer from the payments object
    const offer = leaseOffer || purchaseOffer || cashOffer;
    return {
      offer,
      leaseOffer,
      purchaseOffer,
      cashOffer,
      terms,
      mileages,
      leaseTerms,
    };
  };

  const hasLeaseOffer = () =>
    !!payments?.allOffers
      ?.find(isLeaseOfferList)
      ?.offers.find((offer: any) => offer.mileage === mileage);

  const renderStep = (financeType: FinanceType) => {
    const {
      offer,
      leaseOffer,
      purchaseOffer,
      cashOffer,
      terms,
      mileages,
      leaseTerms,
    } = getPaymentOfferDetails(financeType);

    // returns list of stackable offers with the current selected offer
    const programOffers: IncentivesWithType =
      offer && sampleCar && financeType
        ? getProgramOffersWithTypes(sampleCar, offer, financeType)
        : [];

    // get unique offers by type
    const uniqueProgramOffers = uniqBy(
      programOffers.filter((x) => x.type !== IncentiveType.OTHER),
      (x) => x.type,
    );

    const renderEditFees = (
      <LinkButton
        className={
          (styles.reviewDataNameEditButton, styles.priceBreakdownInput)
        }
        label="Edit Fees"
        to={ROUTES.DEALER_DASHBOARD_PRICING_FEES.getPath()}
        variant="minimal"
        rawLink={false}
        openNewWindow={false}
      />
    );
    return (
      <div key={financeType}>
        <div className={styles.stepCard}>
          <div className={styles.stepCardLeft}>
            <div
              className={classNames(
                // styles.stepContainer,
                styles.pricingValidationInnerStep,
              )}
            >
              <h1>
                <u>{clientTypeToLabel[clientType]}</u> Price Confirmation
              </h1>
              <div className={styles.vehicleContainer}>
                <div>
                  <img
                    src={sampleCar?.carImg || imageCarPlaceholder}
                    alt={`Sample Car ${sampleCar?.carImg}`}
                    width="200"
                  />
                </div>
                {sampleCar && sampleCarRequest && (
                  <div className={styles.updateVehicleContainer}>
                    <h3>{`${sampleCar?.year} ${sampleCar?.make} ${sampleCar?.model}`}</h3>
                    <span id="vin">{`VIN: ${sampleCar?.id}`}</span>
                    <div className={styles.stockButton}>
                      <TextField
                        label="Stock"
                        name="stock"
                        width="sm"
                        // defaultValue={sampleCar?.stock}
                        defaultValue={sampleCarStockInputValue}
                        onChange={(val) => {
                          setSampleCarStockInputValue(val.target.value);
                        }}
                        form={form}
                      />
                      <Button
                        variant="minimal"
                        onClick={() => {
                          if (sampleCarStockInputValue) {
                            setCarStockParameterForRemoteRequest(
                              sampleCarStockInputValue,
                            );
                          }
                        }}
                      >
                        Update Vehicle
                      </Button>
                    </div>
                    {sampleCarRequest.result?.error && (
                      <div className={styles.stockNoCar}>
                        Sorry, the Stock number you entered does not match the
                        vehicle condition you are on
                      </div>
                    )}
                  </div>
                )}
              </div>
              {(leaseOffer || purchaseOffer) && (
                <hr
                  style={{
                    width: '100%',
                    border: 0,
                    borderTop: '1px solid #EEEEEE',
                  }}
                />
              )}
              {leaseOffer && (
                <div className={styles.pricingValidationPurchaseInputs}>
                  <Select
                    label="Term"
                    width="sm"
                    options={
                      leaseTerms?.map((ter: number) => ({
                        label: `${ter} Months`,
                        value: ter,
                      })) ?? []
                    }
                    onChange={(val) => {
                      setLeaseTerm(parseInt(val.target.value, 10));
                    }}
                  />
                  <Select
                    label="Credit Rating"
                    width="sm"
                    options={creditRatingOptions}
                    onChange={(val) => {
                      const str = val.target.value;
                      if (
                        str === CreditRating.EXCELLENT ||
                        str === CreditRating.GOOD ||
                        str === CreditRating.AVERAGE ||
                        str === CreditRating.POOR
                      ) {
                        setCreditRating(str);
                      }
                    }}
                  />
                  <Select
                    label="Mileage"
                    width="sm"
                    options={
                      mileages?.map((mil: number) => ({
                        label: `${mil}`,
                        value: mil,
                      })) ?? []
                    }
                    onChange={(val) => {
                      setMileage(parseInt(val.target.value, 10));
                    }}
                  />
                </div>
              )}
              {purchaseOffer && (
                <>
                  {hasLowInterestAndHighRebate && (
                    <div
                      className={styles.purchaseLowInterestAndHighRebateInputs}
                    >
                      <span className={styles.title}>Incentives Options</span>
                      <Radio
                        className="flex-row"
                        value={lowInterestOrHighRebate}
                        options={[
                          {
                            label: 'Low Interest',
                            value: OfferCategoryType.LOW_INTEREST,
                          },
                          {
                            label: 'High Rebate',
                            value: OfferCategoryType.HIGH_REBATE,
                          },
                        ]}
                        onChange={(val) => {
                          if (
                            val === OfferCategoryType.LOW_INTEREST ||
                            val === OfferCategoryType.HIGH_REBATE
                          ) {
                            setLowInterestOrHighRebate(val);
                          }
                        }}
                      />
                    </div>
                  )}
                  <div className={styles.pricingValidationPurchaseInputs}>
                    <Select
                      label="Term"
                      name="term"
                      width="sm"
                      options={
                        terms?.map((ter: number) => ({
                          label: `${ter} Months`,
                          value: ter,
                        })) ?? []
                      }
                      onChange={(val) => {
                        setTerm(parseInt(val.target.value, 10));
                      }}
                    />
                    <Select
                      label="Credit Rating"
                      name="term"
                      width="sm"
                      options={creditRatingOptions}
                      onChange={(val) => {
                        const str = val.target.value;
                        if (
                          str === CreditRating.EXCELLENT ||
                          str === CreditRating.GOOD ||
                          str === CreditRating.AVERAGE ||
                          str === CreditRating.POOR
                        ) {
                          setCreditRating(str);
                        }
                      }}
                    />
                    <TextField
                      style={{ backgroundColor: 'white' }}
                      label="APR"
                      name="stock"
                      disabled
                      width="sm"
                      value={fmtPercent(purchaseOffer.apr)}
                    />
                  </div>
                </>
              )}
              <hr
                style={{
                  width: '100%',
                  border: 0,
                  borderTop: '1px solid #EEEEEE',
                }}
              />
              {renderPricingCorrect(financeType)}
            </div>
          </div>
          <div className={styles.stepCardRight}>
            {isLoadingPayments && <Spinner variant="fill-container-overlay" />}
            {true && (
              <>
                <div
                  className={classNames(
                    styles.stepContainer,
                    styles.priceBreakdownContainer,
                    styles.priceBreakdownContainerTop,
                  )}
                >
                  <h1>Pricing Breakdown</h1>
                  {renderPriceBreakdownLine('Vehicle Price', offer?.price)}
                  {renderPriceBreakdownLine('MSRP', sampleCar?.msrp)}
                  {financeType === FinanceType.LEASE &&
                    renderPriceBreakdownLine('MRM', sampleCar?.mrm || 0)}
                  {financeType === FinanceType.LEASE &&
                    renderPriceBreakdownLine(
                      'Capitalized Cost',
                      leaseOffer?.capitalizedCost,
                    )}
                  {financeType === FinanceType.LEASE &&
                    renderPriceBreakdownLine('Residual', leaseOffer?.residual)}
                  {financeType === FinanceType.LEASE &&
                    renderPriceBreakdownLineRate(leaseOffer?.rate)}
                  {!!offer?.fees.length &&
                    renderPriceBreakdownCustomLine(
                      'Dealer Fees',
                      renderEditFees,
                    )}
                  {!!offer?.fees.length &&
                    offer.fees.map((fee: any) =>
                      renderPriceBreakdownLine(fee.name, fee.value, true),
                    )}
                  {!isEmpty(offer?.taxBreakdown) && renderPriceBreakdownZip()}
                  {!isEmpty(offer?.taxBreakdown)
                    ? Object.keys(offer?.taxBreakdown || {}).map(
                        (key: string) => {
                          if (
                            key === TaxType.STATE ||
                            key === TaxType.CITY ||
                            key === TaxType.COUNTY
                          ) {
                            return renderPriceBreakdownLine(
                              offer?.taxBreakdown?.[key]?.name || '',
                              offer?.taxBreakdown?.[key]?.amount,
                              true,
                            );
                          }
                          return null;
                        },
                      )
                    : null}
                  {renderPriceBreakdownDownPayment()}
                  {renderPriceBreakdownTradeInAllowance()}
                  {renderPriceBreakdownIncentivesAndRebates(
                    financeType,
                    uniqueProgramOffers,
                  )}
                  <div className={styles.noAddonsDisclaimer}>
                    No Add-Ons Applied
                  </div>
                </div>
                {/* <div className={styles.upperGradientDiv} /> */}
                <div
                  className={classNames(
                    styles.stepContainer,
                    styles.priceBreakdownContainer,
                    styles.priceBreakdownContainerBottom,
                    // styles.upperGradientDiv,
                  )}
                >
                  {financeType === FinanceType.PURCHASE && (
                    <>
                      {renderPriceBreakdownCustomLine('Monthly Payment')}
                      {renderPriceBreakdownLineBigNumber(
                        purchaseOffer?.estimatedMonthlyPayment,
                      )}
                    </>
                  )}
                  {financeType === FinanceType.LEASE && (
                    <>
                      {renderPriceBreakdownCustomLine('Monthly Payment')}
                      {renderPriceBreakdownLineBigNumber(
                        leaseOffer?.estimatedMonthlyPayment,
                      )}
                    </>
                  )}
                  {financeType === FinanceType.LEASE && (
                    <>
                      {renderPriceBreakdownLine(
                        'Due at Start',
                        leaseOffer?.totalAtStart,
                        true,
                      )}
                    </>
                  )}
                  {financeType === FinanceType.CASH && (
                    <>
                      {renderPriceBreakdownCustomLine('Total Payment')}
                      {renderPriceBreakdownLineBigNumber(cashOffer?.totalDue)}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div style={{ padding: '0px 100px' }} className={styles.pageNav}>
          <div className={styles.reportPricingIssueContainer}>
            <Button
              className={styles.pageNavButton}
              label="Previous Step"
              onClick={() => goToPreviousTab(financeType)}
            />
            <div className="spacer" />
            <div className="spacer" />
          </div>
          <div className="spacer" />
          <div className={styles.reportPricingIssueContainer}>
            {!dealer.launchpad?.status?.finishedPricingValidation &&
            ((sampleCar?.condition === Condition.USED &&
              financeType === FinanceType.PURCHASE) ||
              financeType === FinanceType.LEASE) ? (
              <Button
                className={styles.pageNavButton}
                label="Save and Continue"
                type="submit"
                variant="primary"
                loading={formBlock.current.isSubmitting}
              />
            ) : (
              <Button
                className={styles.pageNavButton}
                label="Continue"
                onClick={() =>
                  confirmAndGoToNextTab(financeType, hasLeaseOffer())
                }
                variant="primary"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <form
      className={classNames(styles.stepContainer, styles.pricingValidationStep)}
      onSubmit={onSubmit}
    >
      <Modal
        isOpen={emailLinkModalOpen}
        onRequestClose={() => setEmailLinkModalOpen(false)}
        className={styles.pricingValidationModal}
      >
        <div className={styles.modalTitle}>
          Welcome Back, You&apos;re Almost Done!
        </div>
        <div className={styles.modalDescription}>
          We&apos;ve processed the information that you saved before. Now,
          let&apos;s check to make sure that we&apos;re displaying the correct
          pricing information for your vehicles before we launch!
        </div>
        <div>&nbsp;</div>
        <div className={styles.modalDescription}>
          This is an essential step in our setup process as we want to ensure
          that we&apos;re providing the most accurate pricing models before your
          Upstart Auto Retail experience goes live.
        </div>
        <div className={styles.modalWarning}>
          Please note: Any incorrect settings can cause your vehicles to be
          incorrectly priced in your Upstart Auto Retail solutions.
        </div>
        <div>&nbsp;</div>
        <div className={styles.buttonContainer}>
          <Button
            label="Go To Pricing Validation Tool"
            variant="primary"
            onClick={() => setEmailLinkModalOpen(false)}
          />
        </div>
      </Modal>
      <Modal
        isOpen={pricingNotCorrectModalOpen}
        onRequestClose={() => setPricingNotCorrectModalOpen(false)}
        className={styles.pricingValidationModal}
      >
        <div className={styles.modalTitle}>
          Sorry We Couldn&apos;t Find The Right Price
        </div>
        <div className={styles.modalSubTitle}>
          Please, let us know what went wrong and our support team will get back
          to you
        </div>
        <div className={styles.modalSection}>
          Here Are Some Tips That Might Help You Get The Price
        </div>
        <div>&nbsp;</div>
        <div className={styles.howToGetRightPriceContainer}>
          <div className={styles.howToGetRightPriceItem}>
            <div className={styles.howToGetRightPriceItemTitle}>
              1 - Check Your Fees
            </div>
            <div>&nbsp;</div>
            <div className={styles.howToGetRightPriceItemDescription}>
              Fees directly impact price, ensure you&apos;ve added all necessary
              fees
            </div>
          </div>
          <div className={styles.howToGetRightPriceItem}>
            <div className={styles.howToGetRightPriceItemTitle}>
              2 - Is Your Location Correct?
            </div>
            <div>&nbsp;</div>
            <div className={styles.howToGetRightPriceItemDescription}>
              We use your location to get the appropriate tax rates
            </div>
          </div>
          <div className={styles.howToGetRightPriceItem}>
            <div className={styles.howToGetRightPriceItemTitle}>
              3 - Have a look at your APR
            </div>
            <div>&nbsp;</div>
            <div className={styles.howToGetRightPriceItemDescription}>
              We use your APR values to generate the monthly payment
            </div>
          </div>
        </div>
        <div>&nbsp;</div>
        <div>&nbsp;</div>
        <div className={styles.modalSection}>
          Want Some Help? Tell Us What Went Wrong
        </div>
        <div>&nbsp;</div>
        <textarea
          cols={30}
          rows={10}
          className={styles.wrongPricingTextArea}
          value={wrongPricingValidationText}
          placeholder="Hi, I noticed the pricing was wrong because..."
          onChange={(val) => setWrongPricingValidationText(val.target.value)}
        />
        <div>&nbsp;</div>
        <div>&nbsp;</div>
        <div className={styles.wrongPricingButtonsContainer}>
          <Button
            className={styles.wrongPricingButton}
            label="Close"
            onClick={() => setPricingNotCorrectModalOpen(false)}
          />
          <Button
            className={styles.wrongPricingButton}
            label="Send Help Request"
            variant="primary"
            disabled={!wrongPricingValidationText}
            onClick={() => submitWrongPricingValidationText.execute()}
          />
        </div>
      </Modal>
      <div className={styles.stepTitle}>
        {`${validationType} Vehicle Pricing Validation: ${clientTypeToLabel[clientType]}`}
      </div>
      {sampleCarRequest.loading && <Spinner variant="fill-container" />}
      {sampleCarRequest.result && (
        <Tabs ref={tabsRef} className={classNames(styles.tabs)}>
          {[
            {
              key: FinanceType.CASH,
              name: 'Cash',
              render: () => renderStep(FinanceType.CASH),
            },
            {
              key: FinanceType.PURCHASE,
              name: 'Finance',
              render: () => renderStep(FinanceType.PURCHASE),
            },
            ...(sampleCar?.condition === Condition.NEW && hasLeaseOffer()
              ? [
                  {
                    key: FinanceType.LEASE,
                    name: 'Lease',
                    render: () => renderStep(FinanceType.LEASE),
                  },
                ]
              : []),
          ]}
        </Tabs>
      )}
    </form>
  );
};
